import React, { useMemo, useEffect } from 'react';
import { Link, RouteComponentProps, useLocation } from 'react-router-dom';
import { WithT } from 'i18next';
import { IModuleRoute, IRoute, AppRouteNamesList } from 'models';
import {
	getFromSessionStorage,
	removeFromSessionStorage,
	saveToSessionStorage,
	SessionStorageKeyTypes,
} from 'utils';
import { flatRoutes } from 'helpers';
import './breadcrumbs.scss';
import {
	serviceIdStore,
	useServiceIdStore,
} from '../../modules/admin-panel/components/comments-block/components/ai-insights/actions/store/ActionServiceAcceptedActionsStore';

interface BreadcrumbsProps
	extends RouteComponentProps<{
			id: string;
			surveyId?: string;
		}>,
		WithT {
	prevLocation: IRoute[];
	updatePrevLocation: (crumbs: IRoute[]) => void;
	moduleRoutes: IModuleRoute[];
	currPageName?: string;
	showSingleCrumb?: boolean;
	surveyName?: string;
}

export function Breadcrumbs({
	updatePrevLocation,
	match: { path: nextPath, params },
	t,
	prevLocation,
	moduleRoutes,
	currPageName,
	showSingleCrumb,
	surveyName,
}: BreadcrumbsProps) {
	const routes = moduleRoutes
		.map((moduleRoute) => flatRoutes(moduleRoute))
		.flat();
	const crumbs = useMemo(
		() =>
			routes
				.filter(({ path }) => nextPath.includes(path))
				.map((crumb, i, arr) => {
					let url = crumb.path.replace(/:id/, params.id);
					if (params.surveyId) {
						url = crumb.path.replace(/:surveyId/, params.surveyId);
					}

					const newCrumb = {
						...crumb,
						path: url,
					};

					if (prevLocation.length) {
						const similarLocation = prevLocation.find(
							(location) => location.name === crumb.name,
						);

						newCrumb.customTitle = similarLocation?.customTitle;
					}

					if (
						newCrumb.name === AppRouteNamesList.dashboard &&
						!newCrumb.customTitle
					) {
						newCrumb.customTitle = surveyName;
					}

					if (currPageName && arr.length - 1 === i) {
						newCrumb.customTitle = currPageName;
					}

					return newCrumb;
				}),
		[routes, nextPath, params, prevLocation, currPageName, surveyName],
	);

	useEffect(() => {
		if (
			prevLocation.length &&
			prevLocation[0].name !== AppRouteNamesList.settings
		) {
			removeFromSessionStorage(SessionStorageKeyTypes.breadCrumbs);
			saveToSessionStorage(SessionStorageKeyTypes.breadCrumbs, prevLocation);
		}

		updatePrevLocation(crumbs);
	}, [nextPath]);

	const sessionCrumbs = useMemo(() => {
		const sCrumbs = Object.values(
			getFromSessionStorage(SessionStorageKeyTypes.breadCrumbs) || {},
		);

		return (sCrumbs as IRoute[]).reduce((resultSCrumbs, currSCrumb) => {
			const hasCrumb = crumbs.find(({ path }) => path === currSCrumb.path);

			if (!hasCrumb) {
				resultSCrumbs.push(currSCrumb);
			}

			return resultSCrumbs;
		}, [] as Array<IRoute>);
	}, [crumbs]);

	const isSettings = useMemo(
		() => crumbs[0] && crumbs[0].name === AppRouteNamesList.settings,
		[crumbs],
	);

	const isSurveyTracking = useMemo(
		() => crumbs[1] && crumbs[1].name === AppRouteNamesList.surveyTracking,
		[crumbs],
	);

	const wasSurveyEdited = useMemo(
		() =>
			sessionCrumbs.length === 3 &&
			sessionCrumbs[2].name === AppRouteNamesList.surveyScheduling,
		[sessionCrumbs],
	);

	if (crumbs.length <= 1 && !showSingleCrumb && !isSettings) {
		return null;
	}

	if (isSettings) {
		if (
			isSurveyTracking &&
			sessionCrumbs.length === 2 &&
			sessionCrumbs[1].name === AppRouteNamesList.clientsDetails
		) {
			crumbs.splice(0, 1);
			crumbs.unshift(...sessionCrumbs);
		}

		if (wasSurveyEdited) {
			const fromClientPage =
				prevLocation[0] && prevLocation[0].name === AppRouteNamesList.clients;
			fromClientPage
				? crumbs.splice(0, 2, ...prevLocation)
				: crumbs.splice(0, 3);
			removeFromSessionStorage(SessionStorageKeyTypes.breadCrumbs);
			saveToSessionStorage(SessionStorageKeyTypes.breadCrumbs, prevLocation);
		}
	}

	return (
		<ul className="breadcrumb-list">
			{crumbs.map(({ name, path, customTitle }) => {
				return (
					<BreadcrumbItem
						key={path}
						path={path}
						text={customTitle || t(`pages.${name}`)}
						surveyName={surveyName}
					/>
				);
			})}
			{crumbs.length === 1 && crumbs[0].name === 'results' && (
				<BreadcrumbItem
					key={crumbs[0].path}
					path={crumbs[0].path}
					text={t('pages.choose_survey')}
					surveyName={surveyName}
				/>
			)}
		</ul>
	);
}

interface BreadcrumbItemProps {
	path: string;
	text: string;
	surveyName?: string;
}

const BreadcrumbItem: React.FC<BreadcrumbItemProps> = ({
	path,
	text,
	surveyName,
}) => {
	const activeRoundNumberStore = useServiceIdStore(
		(state: serviceIdStore) => state.activeRoundNumberStore,
	);
	const amountRoundNumberStore = useServiceIdStore(
		(state: serviceIdStore) => state.amountRoundNumberStore,
	);
	const { state } = useLocation<
		Record<string, string | number | boolean | undefined>
	>();
	if (!path) {
		return null;
	}
	return (
		<li className="breadcrumb-item">
			<Link to={{ pathname: path, state }}>
				{text === surveyName && activeRoundNumberStore !== 0 // activeRoundNumberStore === 0 prevents "round 0" from displaying when round number is loading in
					? text.concat(
							` Round ${activeRoundNumberStore} of ${amountRoundNumberStore}`,
					  )
					: text}
			</Link>
		</li>
	);
};
